import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { KeyRound, X, Check, ArrowRight } from "lucide-react";

export default function AgentLogin() {
  const [lang] = useState(() => localStorage.getItem("lang") || "en");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      toast.success("Welcome back");
      navigate("/agent/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/agent/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid place-items-center px-6 grain">
      <div className="w-full max-w-md relative z-10">
        <Link to="/" className="font-display text-lg font-medium tracking-tight flex items-center gap-2 mb-10">
          <span className="w-6 h-6 grid place-items-center bg-black text-white font-black">Æ</span>
          Aegis Realtor
        </Link>
        <h1 className="font-display text-4xl font-black tracking-tighter mb-2">{t(lang, "signIn")}</h1>
        <p className="text-sm text-muted-foreground mb-8">{t(lang, "forAgents")}</p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground">{t(lang, "email")}</label>
            <input
              data-testid="login-email" type="email" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full mt-2 px-4 py-3 bg-white hairline focus:border-black outline-none"
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground">{t(lang, "password")}</label>
            <input
              data-testid="login-password" type="password" required
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full mt-2 px-4 py-3 bg-white hairline focus:border-black outline-none"
            />
          </div>
          <button
            data-testid="login-submit" disabled={busy}
            className="w-full py-3 bg-black text-white font-bold tracking-wide hover:opacity-90 disabled:opacity-40 transition-opacity"
          >{t(lang, "signIn")}</button>
        </form>

        {/* Forgot password — deliberately more prominent than a plain link */}
        <button
          data-testid="forgot-open"
          type="button"
          onClick={() => setShowForgot(true)}
          className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 hairline bg-[var(--paper-2)] hover:bg-[var(--paper)] transition-colors text-sm font-bold tracking-wide"
          style={{ borderLeft: "3px solid #d97706" }}
        >
          <KeyRound className="w-4 h-4" strokeWidth={2} />
          Şifremi unuttum · Forgot password
        </button>

        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-[var(--line)]" />
          <span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">{t(lang, "or")}</span>
          <div className="flex-1 h-px bg-[var(--line)]" />
        </div>

        <button data-testid="login-google" onClick={google} className="w-full py-3 hairline bg-white font-bold hover:bg-[var(--paper-2)] transition-colors">
          {t(lang, "google")}
        </button>

        <p className="text-sm text-muted-foreground mt-8">
          {t(lang, "noAccount")}{" "}
          <Link data-testid="link-register" to="/agent/register" className="text-black underline underline-offset-4">
            {t(lang, "createAccount")}
          </Link>
        </p>
      </div>

      {showForgot && <ForgotModal initialEmail={email} onClose={() => setShowForgot(false)} navigate={navigate} />}
    </div>
  );
}

function ForgotModal({ initialEmail, onClose, navigate }) {
  const [step, setStep] = useState(1); // 1=request, 2=verify+reset
  const [email, setEmail] = useState(initialEmail || "");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const sendCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot", { email });
      toast.success("If that email is registered, a recovery code has been generated");
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirm) return toast.error("Passwords do not match");
    if (newPassword.length < 6) return toast.error("Password must be at least 6 characters");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset", { email, code, new_password: newPassword });
      if (data.token) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));
      }
      toast.success("Password updated — you are signed in");
      onClose();
      navigate("/agent/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-6 bg-black/40 backdrop-blur-sm" onClick={() => !busy && onClose()}>
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-white hairline p-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="forgot-modal"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-1">Password recovery</div>
            <div className="font-display text-2xl font-medium">{step === 1 ? "Send me a code" : "Enter code + new password"}</div>
          </div>
          <button data-testid="forgot-close" onClick={onClose} className="text-muted-foreground hover:text-black"><X className="w-4 h-4" /></button>
        </div>

        {step === 1 ? (
          <form onSubmit={sendCode} className="space-y-4">
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground">Email</label>
              <input
                data-testid="forgot-email" type="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-2 px-4 py-3 hairline focus:border-black outline-none"
              />
            </div>
            <p className="text-xs text-muted-foreground">A 6-digit code will be generated. Until email delivery is enabled, ask the platform admin to forward it to you.</p>
            <button data-testid="forgot-send" disabled={busy} className="w-full flex items-center justify-center gap-2 py-3 bg-black text-white font-bold disabled:opacity-40">
              {busy ? "Sending…" : (<>Send code <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>
        ) : (
          <form onSubmit={doReset} className="space-y-4">
            <div className="text-xs text-muted-foreground">Code sent for <span className="text-black font-mono">{email}</span></div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground">Recovery code</label>
              <input
                data-testid="reset-code" required maxLength={6} inputMode="numeric" pattern="[0-9]{6}"
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="w-full mt-2 px-4 py-3 hairline focus:border-black outline-none font-mono text-lg tracking-[0.5em] text-center"
                placeholder="••••••"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground">New password</label>
              <input data-testid="reset-password" type="password" required minLength={6}
                value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                className="w-full mt-2 px-4 py-3 hairline focus:border-black outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground">Confirm password</label>
              <input data-testid="reset-confirm" type="password" required minLength={6}
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="w-full mt-2 px-4 py-3 hairline focus:border-black outline-none"
              />
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setStep(1)} className="px-4 py-3 hairline text-sm">Back</button>
              <button data-testid="reset-submit" disabled={busy} className="flex-1 flex items-center justify-center gap-2 py-3 bg-black text-white font-bold disabled:opacity-40">
                {busy ? "Resetting…" : (<><Check className="w-4 h-4" /> Reset password</>)}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
}
